import { mx } from '../globals';

export type ConfigSettings = ProdFrontendConfig & ExtendedBySetupConfig & OnlyDevConfig;

type ProdFrontendConfig = {
	dataSource: DataSource;
	exportSource: ExportSource;
	restHosts: RestHosts;
	reportingHosts: ReportingHosts;
	imgHosts: ImgHosts;
	authentication: Authentication;
	helpUri: string;
	integration: Integration;
	pageTitle: string;
	basePath: string;
	productId: string;
	maxWordsPerCondition: number;
	search: Search;
	toolbar: Toolbar;
	onlineHelpBasePath: string;
	favicons?: FaviconsEntity[] | null;
	currency: CurrencyConfig;
	linkAllowProtocols?: string[] | null;
	defaultWindowTitles?: DefaultWindowTitles[];
	culture: CultureConfig;

	telemetryToken?: string;
	debugTelemetry?: boolean;
	customerName?: string;
	customerId?: string;
	envType?: string;
	envId?: string;
	licenseId?: string;
	disableExternalAttachmentsPreview: boolean;
	navigationBadgeRefreshTime: number;
	openViewsFullscreen?: boolean;
	expandColumnSearchByDefault: boolean;
	dataSourcesCacheLifetime: number;
	hideTicketCreationFromGlobalSearch: boolean;
	ariaUseHeadingForBannerRegion: boolean;
	enhancedScreenReaderSupport: boolean;

	productTours?: {
		allowedUserData: string; // like: "FirstName,LastName,Email"
	};

	certificates?: Certificates;

	aiAssistantName: string;
};

type CultureConfig = {
	allowAutoTranslate: boolean;
	allowAutoCorrection: boolean;
};

type DefaultWindowTitles = {
	title: string;
	lcid: number;
};

type ExtendedBySetupConfig = {
	productName: string;
	productVersion?: string;
};

type OnlyDevConfig = {
	debug: boolean;
	mode: 'dev' | 'prod';
	skipCaching: boolean;
	devFrontendWorkspaces: Array<string>;
};

type DataSource = {
	pageSize: number;
	pageSizes: number[] | null;
	showHiddenValuesInSearch: boolean;
};
type ExportSource = {
	maxRowSize: number;
};
type RestHosts = {
	default: string;
	onlineHelp: string;
	[hostName: string]: string;
};
type ReportingHosts = {
	default: string;
};
type ImgHosts = {
	default: string;
	rootFolder: string[];
	attachments: string;
	flags: string;
};
type Authentication = {
	oauth2: Oauth2;
	active: boolean;
	params: Params;
	passThrough: string;
};
type Oauth2 = {
	clientid: string;
	scope: string;
	endpoints: Endpoints;
	redirect_uri: string;
	auth_with_implicit: boolean;
	persistent: boolean;
};
type Endpoints = {
	authorize: string;
	token: string;
	signout: string;
	stsLogout: string;
};
type Params = {
	autoLogin: boolean;
	allowNonWindowsAutoLogin: boolean;
	authenticationRedirectUriSuffix: string | null;
};
type Integration = {
	silverback: Silverback;
};
type Silverback = {
	inventoryUrl: string;
};
type Search = {
	maxRecentSearches: number;
};
type Toolbar = {
	showFeedback: boolean;
	showAvatar: boolean;
	showSearch: boolean;
	showAppsButton: boolean;
	showOperationMonitor: boolean;
};
type FaviconsEntity = {
	rel: string;
	href: string;
	type: string;
};
type Certificates = {
	mainCertificateExpiresSoon: boolean;
	mainCertificateValidUntil?: Date | null;
};
export type CurrencyConfig = {
	default: string;
	availableCurrencies?: AvailableCurrenciesEntity[] | null;
	showCurrencyCode: boolean;
};
type AvailableCurrenciesEntity = {
	symbol: string;
	code: string;
};

/**
 * @ngdoc type
 * @name mx.shell.Config
 * @module mx.shell
 *
 * @property = {object} settings - A read-only property that expose an object with all configuration settings
 *
 * @description
 * Provides access to application configuration properties.<br>
 * It's a singleton with no public constructor available.<br>
 * Configuration object is obtained via a service
 * This service exposes single configuration object methods and properties
 */
export class ConfigService {
	public readonly settings: ConfigSettings;
	public readonly isDebugMode: boolean;

	constructor() {
		this.settings = mx.shell.config;

		if (!this.settings.basePath) {
			this.settings.basePath = '/';
		}

		const defaultPageSizes = [10, 20, 50, 100, 200];

		if (typeof this.settings.dataSource !== 'object') {
			this.settings.dataSource = {
				pageSize: defaultPageSizes[1],
				pageSizes: defaultPageSizes,
				showHiddenValuesInSearch: false,
			};
		} else {
			if (!Array.isArray(this.settings.dataSource.pageSizes) || this.settings.dataSource.pageSizes.length === 0) {
				this.settings.dataSource.pageSizes = defaultPageSizes;
			}
			if (typeof this.settings.dataSource.pageSize !== 'number' || !this.settings.dataSource.pageSizes.includes(this.settings.dataSource.pageSize)) {
				this.settings.dataSource.pageSize = this.settings.dataSource.pageSizes[0];
			}
		}

		if (typeof this.settings.maxWordsPerCondition !== 'number') {
			this.settings.maxWordsPerCondition = 50;
		}

		if (typeof this.settings.disableExternalAttachmentsPreview !== 'boolean') {
			this.settings.disableExternalAttachmentsPreview = false;
		}

		if (typeof this.settings.navigationBadgeRefreshTime !== 'number') {
			this.settings.navigationBadgeRefreshTime = 600000;
		}

		if (typeof this.settings.openViewsFullscreen !== 'boolean') {
			this.settings.openViewsFullscreen = true;
		}

		if (typeof this.settings.expandColumnSearchByDefault !== 'boolean') {
			this.settings.expandColumnSearchByDefault = true;
		}

		if (typeof this.settings.dataSourcesCacheLifetime !== 'number') {
			this.settings.dataSourcesCacheLifetime = 30;
		}

		if (typeof this.settings.hideTicketCreationFromGlobalSearch !== 'boolean') {
			this.settings.hideTicketCreationFromGlobalSearch = false;
		}

		if (!this.settings.imgHosts) {
			const baseUrl = this.settings.restHosts.default.replace(/\/m42Services/i, '');
			this.settings.imgHosts = {
				default: baseUrl,
				rootFolder: ['/WM/Images/'],
				attachments: baseUrl,
				flags: baseUrl,
			};
		} else if (typeof this.settings.imgHosts.rootFolder === 'string') {
			this.settings.imgHosts.rootFolder = [this.settings.imgHosts.rootFolder];
		}

		if (!this.settings.exportSource || typeof this.settings.exportSource !== 'object') {
			this.settings.exportSource = {
				maxRowSize: 65000,
			};
		} else {
			if (typeof this.settings.exportSource.maxRowSize !== 'number') {
				this.settings.exportSource.maxRowSize = 65000;
			}
		}

		if (typeof this.settings.culture !== 'object') {
			this.settings.culture = {
				allowAutoTranslate: false,
				allowAutoCorrection: false,
			};
		} else {
			if (typeof this.settings.culture.allowAutoTranslate !== 'boolean') {
				this.settings.culture.allowAutoTranslate = false;
			}
			if (typeof this.settings.culture.allowAutoCorrection !== 'boolean') {
				this.settings.culture.allowAutoCorrection = false;
			}
		}

		if (typeof this.settings.ariaUseHeadingForBannerRegion !== 'boolean') {
			this.settings.ariaUseHeadingForBannerRegion = true;
		}

		if (typeof this.settings.enhancedScreenReaderSupport !== 'boolean') {
			this.settings.enhancedScreenReaderSupport = true;
		}

		if (!this.settings.aiAssistantName) {
			this.settings.aiAssistantName = 'Matrix42 Neo';
		}

		if (!this.settings.certificates) {
			this.settings.certificates = {
				mainCertificateExpiresSoon: false,
				mainCertificateValidUntil: null,
			};
		} else {
			this.settings.certificates.mainCertificateExpiresSoon = this.settings.certificates.mainCertificateExpiresSoon === true;

			if (!(this.settings.certificates.mainCertificateValidUntil instanceof Date)) {
				this.settings.certificates.mainCertificateValidUntil = null;
			}
		}

		this.isDebugMode = Boolean(this.settings.debug);
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.Config#get
	 *
	 * @description
	 * Returns the value of the configuration parameter
	 *
	 * @param {string} name - name of the configuration parameter
	 */
	public get<Key extends keyof ConfigSettings>(name: Key) {
		return this.settings[name];
	}
}
